import axios from "axios";
import CustomToast from "components/ui/Toast";
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { API } from "middleware/api";
import { parseJwt } from "utils/parseJWT";

const AuthContext = createContext();

const AuthProvider = ({ children }) => {
  const toast = useRef(null);
  const [toastOptions, setToastOptions] = useState();

  // State to hold the authentication token
  const [accessToken, setAccessToken] = useState(
    localStorage.getItem("accessBNNToken")
  );

  const [userDetails, setUserDetails] = useState();
  const [userAccess, setUserAccess] = useState();
  const [accessType, setAccessType] = useState();
  const [loginType, setLoginType] = useState(parseJwt(accessToken)?.loginType);

  // Function to set the authentication token
  const setToken = (newToken) => {
    localStorage.setItem("accessBNNToken", newToken);
    setAccessToken(newToken);
  };

  const removeToken = () => {
    setToastOptions({
      severity: "success",
      summary: "Logout Success!",
      detail: "You have been successfully logged out of portal!",
    });
    setAccessToken(null);
    setUserDetails(null);
    setUserAccess(null);
    localStorage.removeItem("accessBNNToken");
  };

  useEffect(() => {
    const fetchUser = async (email, loginUser) => {
      try {
        let user;

        if (!userDetails) {
          if (!["nagar_nigam", "jalkal"].includes(loginUser)) {
            user = await API.fetchUploadUser(email);
          } else {
            user = await API.fetchOfficial(email);
          }

          if (user) {
            setUserDetails(user);
          } else {
            setToastOptions({
              severity: "warn",
              summary: "Oops...",
              detail: "Could not load user correctly, please login again",
            });
            removeToken();
          }
        }
      } catch (error) {
        setToastOptions({
          severity: "error",
          summary: "Error",
          detail: error.message,
        });
        removeToken();
      }
    };

    if (accessToken) {
      const decodedJWT = parseJwt(accessToken);

      // ! Calculate expiration time in milliseconds (8 hours)
      const threeHoursInMs = 8 * 60 * 60 * 1000;
      const expirationTime = decodedJWT?.exp * threeHoursInMs;

      // ! Check if current time is greater than expiration time
      if (expirationTime <= new Date().getTime()) {
        removeToken();
      }

      axios.defaults.headers.common["Authorization"] = `Bearer ${accessToken}`;
      setToken(accessToken);

      setLoginType(decodedJWT.loginType);
      setUserAccess(decodedJWT.accessName);
      setAccessType(decodedJWT.accessType);

      fetchUser(decodedJWT?.email, decodedJWT?.loginType);
    } else {
      delete axios.defaults.headers.common["Authorization"];
      localStorage.removeItem("accessBNNToken");
    }
  }, [accessToken, userDetails]);

  const contextValue = useMemo(
    () => ({
      accessToken,
      setToken,
      removeToken,
      userDetails,
      userAccess,
      loginType,
      accessType,
    }),
    [accessToken, userDetails, userAccess, loginType, accessType]
  );

  // Provide the authentication context to the children components
  return (
    <>
      <CustomToast ref={toast} toastOptions={toastOptions} />
      <AuthContext.Provider value={contextValue}>
        {children}
      </AuthContext.Provider>
    </>
  );
};

export default AuthProvider;

export const useAuth = () => {
  return useContext(AuthContext);
};
